if (!customElements.get("collection-tabs-showcase")) {
  customElements.define(
    "collection-tabs-showcase",
    class CollectionTabsShowcase extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this._onPointerDown = this._onPointerDown.bind(this);
        this._onPointerMove = this._onPointerMove.bind(this);
        this._onPointerUp = this._onPointerUp.bind(this);
        this._onClickCapture = this._onClickCapture.bind(this);
        this._onDragStart = this._onDragStart.bind(this);
        this._drag = null;
        this._frame = 0;
      }

      connectedCallback() {
        this._tabs = Array.from(this.querySelectorAll("[data-cts-tab]"));
        this._panels = Array.from(this.querySelectorAll("[data-cts-panel]"));

        this.addEventListener("click", this._onClick);
        this.addEventListener("keydown", this._onKeydown);
        this.addEventListener("click", this._onClickCapture, true);

        this._panels.forEach((panel) => {
          const track = panel.querySelector("[data-cts-track]");
          if (!track) return;
          track.addEventListener("scroll", this._onScroll, { passive: true });
          track.addEventListener("pointerdown", this._onPointerDown);
          track.addEventListener("dragstart", this._onDragStart);
          this._updateArrows(panel);
        });
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onClick);
        this.removeEventListener("keydown", this._onKeydown);
        this.removeEventListener("click", this._onClickCapture, true);
        document.removeEventListener("pointermove", this._onPointerMove);
        document.removeEventListener("pointerup", this._onPointerUp);
        document.removeEventListener("pointercancel", this._onPointerUp);
        cancelAnimationFrame(this._frame);
      }

      _onClick(event) {
        const tab = event.target.closest("[data-cts-tab]");
        if (tab && this.contains(tab)) {
          this._activate(this._tabs.indexOf(tab), { focus: false });
          return;
        }

        const panel = event.target.closest("[data-cts-panel]");
        if (!panel) return;

        if (event.target.closest("[data-cts-prev]")) {
          this._scrollTrack(panel, -1);
        } else if (event.target.closest("[data-cts-next]")) {
          this._scrollTrack(panel, 1);
        } else if (event.target.closest("[data-cts-more]")) {
          this._revealMore(panel);
        }
      }

      _onKeydown(event) {
        const current = event.target.closest("[data-cts-tab]");
        if (!current || !this.contains(current)) return;

        const index = this._tabs.indexOf(current);
        const last = this._tabs.length - 1;
        let next = -1;

        switch (event.key) {
          case "ArrowLeft":
            next = index === 0 ? last : index - 1;
            break;
          case "ArrowRight":
            next = index === last ? 0 : index + 1;
            break;
          case "Home":
            next = 0;
            break;
          case "End":
            next = last;
            break;
          case "Enter":
          case " ":
            next = index;
            break;
          default:
            return;
        }

        event.preventDefault();
        this._activate(next, { focus: true });
      }

      _activate(index, { focus }) {
        if (index < 0 || !this._tabs[index]) return;

        this._tabs.forEach((tab, i) => {
          const selected = i === index;
          tab.setAttribute("aria-selected", selected ? "true" : "false");
          tab.setAttribute("tabindex", selected ? "0" : "-1");
        });

        this._panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });

        this._updateArrows(this._panels[index]);

        const tab = this._tabs[index];
        const tablist = tab.parentElement;
        tablist.scrollTo({
          left: tab.offsetLeft - (tablist.clientWidth - tab.offsetWidth) / 2,
          behavior: "smooth",
        });

        if (focus) this._tabs[index].focus();
      }

      _scrollTrack(panel, direction) {
        const track = panel.querySelector("[data-cts-track]");
        const card = track?.querySelector(".collection-tabs-showcase__card");
        if (!track || !card) return;

        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const step = card.getBoundingClientRect().width + gap;
        const cards = Math.max(1, Math.floor(track.clientWidth / step));
        track.scrollBy({ left: direction * step * cards });
      }

      _onScroll(event) {
        const panel = event.target.closest("[data-cts-panel]");
        cancelAnimationFrame(this._frame);
        this._frame = requestAnimationFrame(() => this._updateArrows(panel));
      }

      _updateArrows(panel) {
        const track = panel?.querySelector("[data-cts-track]");
        if (!track) return;

        const prev = panel.querySelector("[data-cts-prev]");
        const next = panel.querySelector("[data-cts-next]");
        const max = track.scrollWidth - track.clientWidth;

        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= max - 1;
      }

      _revealMore(panel) {
        const hidden = panel.querySelectorAll(
          "[data-cts-batch]:not([data-cts-revealed])",
        );
        if (hidden.length) {
          const batch = hidden[0].dataset.ctsBatch;
          hidden.forEach((card) => {
            if (card.dataset.ctsBatch === batch) card.dataset.ctsRevealed = "";
          });
        }

        if (panel.querySelector("[data-cts-batch]:not([data-cts-revealed])"))
          return;

        panel.querySelector("[data-cts-more]").hidden = true;
        panel.querySelector("[data-cts-view-all]").hidden = false;
      }

      // Images and links start a native drag that would cancel the pointer drag.
      _onDragStart(event) {
        event.preventDefault();
      }

      // Mouse drag to scroll; touch and pen already scroll natively.
      _onPointerDown(event) {
        if (event.pointerType !== "mouse" || event.button !== 0) return;

        const track = event.currentTarget;
        this._drag = {
          track,
          startX: event.clientX,
          startScroll: track.scrollLeft,
          moved: false,
        };
        document.addEventListener("pointermove", this._onPointerMove);
        document.addEventListener("pointerup", this._onPointerUp);
        document.addEventListener("pointercancel", this._onPointerUp);
      }

      _onPointerMove(event) {
        const drag = this._drag;
        if (!drag) return;

        const delta = event.clientX - drag.startX;
        if (!drag.moved && Math.abs(delta) > 5) {
          drag.moved = true;
          drag.track.classList.add("is-dragging");
        }
        if (drag.moved) drag.track.scrollLeft = drag.startScroll - delta;
      }

      _onPointerUp() {
        const drag = this._drag;
        document.removeEventListener("pointermove", this._onPointerMove);
        document.removeEventListener("pointerup", this._onPointerUp);
        document.removeEventListener("pointercancel", this._onPointerUp);
        if (!drag) return;

        drag.track.classList.remove("is-dragging");
        // Keep the flag until the click that follows the drag has been swallowed.
        this._justDragged = drag.moved;
        this._drag = null;
        setTimeout(() => {
          this._justDragged = false;
        }, 0);
      }

      _onClickCapture(event) {
        if (!this._justDragged) return;
        event.preventDefault();
        event.stopPropagation();
        this._justDragged = false;
      }
    },
  );
}
